/**
 * ReviewHistoryList — self-contained stateful timeline of approval + run events.
 *
 * Fetches from:
 *   GET /v1/runs/:id/events        — run-level events (Go production)
 *   GET /v1/approvals?runId=:id    — approval gate records (Go production)
 *
 * Both endpoints return Go's standard list envelope:
 *   { items: T[], page: { nextCursor: string | null } }
 */
import { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import { RefreshCw, AlertTriangle, Loader2 } from 'lucide-react'
import { api, apiErrorMessage } from '../../api/client'
import type { RunEvent, ApprovalGate, GoListResponse } from '../../api/types'

// ── Public prop types ─────────────────────────────────────────────────────────

export interface ReviewHistoryListProps {
  runId: string
  /**
   * Change this value to trigger a re-fetch (e.g. pass `submitting` state from
   * ReviewSidebar so the list refreshes after a decision is submitted).
   */
  refreshKey?: unknown
  className?: string
}

/** @deprecated — pass runId directly; the component self-fetches */
export type ReviewHistoryEntry = {
  id: string
  reviewer: string
  action: 'approved' | 'rejected' | 'commented'
  comment?: string
  timestamp: string
}

// ── Internal normalized type ──────────────────────────────────────────────────

type NormalizedEntry = {
  id: string
  kind: 'approved' | 'rejected' | 'expired' | 'run_event'
  label: string
  actor: string
  actorType: 'user' | 'system' | 'agent'
  notes?: string
  gateId?: string
  rawEventType: string
  timestamp: string
  isApproval: boolean
}

// ── Colour maps ───────────────────────────────────────────────────────────────

const DOT_COLORS: Record<NormalizedEntry['kind'], string> = {
  approved:  '#4ade80',
  rejected:  '#f87171',
  expired:   '#94a3b8',
  run_event: 'rgba(148,163,184,0.30)',
}

const LABEL_COLORS: Record<NormalizedEntry['kind'], string> = {
  approved:  'text-[#4ade80]',
  rejected:  'text-[#f87171]',
  expired:   'text-[#94a3b8]',
  run_event: 'text-[#4e6480]',
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  if (diff < 60_000)     return `${Math.floor(diff / 1_000)}s ago`
  if (diff < 3_600_000)  return `${Math.floor(diff / 60_000)}m ago`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`
  return new Date(iso).toLocaleDateString()
}

function prettifyRunSuffix(eventType: string): string {
  const suffix = eventType.replace(/^run\./, '')
  if (!suffix || suffix === eventType) return eventType
  // 'status_changed' → 'Status changed', 'created' → 'Run created', etc.
  if (suffix === 'created') return 'Run created'
  if (suffix === 'started') return 'Run started'
  return suffix.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
}

/**
 * Normalise RunEvent[] into display entries.
 *
 * Go's RunEvent has no actorId/actorType — those fields are on AuditLog
 * (governance events).  We fall back to 'system' for both so the actor
 * chip always renders.  If the payload includes actor hints (e.g. from
 * the mock server which injects actorId) they are used preferentially.
 */
function normalizeEvents(
  events: RunEvent[],
  _gateById: Map<string, ApprovalGate>,
): NormalizedEntry[] {
  return events
    .filter((e) => e.eventType.startsWith('approval.') || e.eventType.startsWith('run.'))
    .map((e): NormalizedEntry => {
      let kind: NormalizedEntry['kind']
      if (e.eventType === 'approval.approved')      kind = 'approved'
      else if (e.eventType === 'approval.rejected') kind = 'rejected'
      else if (e.eventType === 'approval.expired')  kind = 'expired'
      else                                           kind = 'run_event'

      let label: string
      if (kind === 'approved')      label = 'Approved'
      else if (kind === 'rejected') label = 'Rejected'
      else if (kind === 'expired')  label = 'Expired'
      else                          label = prettifyRunSuffix(e.eventType)

      const notes =
        (e.payload?.notes as string | undefined) ??
        (e.payload?.reason as string | undefined) ??
        (e.payload?.comment as string | undefined)

      // actorId / actorType are not on Go's RunEvent — check payload hints
      // or fall back to 'system'.
      const actorId   = (e.payload?.actorId   as string | undefined) ?? 'system'
      const actorType = (e.payload?.actorType  as 'user' | 'system' | 'agent' | undefined) ?? 'system'

      // prefer occurredAt (Go), fall back to createdAt
      const timestamp = e.occurredAt ?? e.createdAt

      return {
        id:           e.id,
        kind,
        label,
        actor:        actorId,
        actorType,
        notes,
        gateId:       e.payload?.gateId as string | undefined,
        rawEventType: e.eventType,
        timestamp,
        isApproval:   kind !== 'run_event',
      }
    })
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
}

// ── Container / item animation variants ──────────────────────────────────────

const listVariants = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.05 } },
}

const itemVariants = {
  hidden: { opacity: 0, x: -8 },
  show:   { opacity: 1, x: 0 },
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ReviewHistoryList({ runId, refreshKey, className = '' }: ReviewHistoryListProps) {
  const [entries,     setEntries]     = useState<NormalizedEntry[]>([])
  const [total,       setTotal]       = useState(0)
  const [nextCursor,  setNextCursor]  = useState<string | null>(null)
  const [loading,     setLoading]     = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error,       setError]       = useState<string | null>(null)

  const load = useCallback(async (append = false, cursor?: string) => {
    if (append) setLoadingMore(true)
    else        { setLoading(true); setError(null) }

    try {
      const eventsParams: Record<string, string | number> = { limit: 25 }
      if (cursor) eventsParams.cursor = cursor

      // ── Go production endpoints ──────────────────────────────────────────
      // GET /v1/runs/:id/events  → GoListResponse<RunEvent>
      // GET /v1/approvals?runId= → GoListResponse<ApprovalGate>
      const [eventsResult, approvalsResult] = await Promise.allSettled([
        api.get<GoListResponse<RunEvent>>(
          `/v1/runs/${encodeURIComponent(runId)}/events`,
          { params: eventsParams },
        ),
        api.get<GoListResponse<ApprovalGate>>(
          '/v1/approvals',
          { params: { runId } },
        ),
      ])

      const eventsData     = eventsResult.status     === 'fulfilled' ? eventsResult.value     : null
      const approvalsData  = approvalsResult.status  === 'fulfilled' ? approvalsResult.value  : null

      if (!eventsData && !approvalsData) {
        throw new Error('Failed to load history')
      }

      const gateById = new Map<string, ApprovalGate>(
        (approvalsData?.items ?? []).map((g) => [g.id, g]),
      )

      const normalized = normalizeEvents(eventsData?.items ?? [], gateById)

      setEntries((prev) => append ? [...prev, ...normalized] : normalized)
      setTotal(normalized.length)
      // Go pagination: cursor lives at page.nextCursor (nested)
      setNextCursor(eventsData?.page.nextCursor ?? null)
    } catch (err) {
      if (!append) setError(apiErrorMessage(err, 'Failed to load history'))
    } finally {
      if (append) setLoadingMore(false)
      else        setLoading(false)
    }
  }, [runId])

  useEffect(() => {
    if (!runId) return
    void load(false)
  }, [runId, refreshKey, load])

  const handleRefresh  = () => void load(false)
  const handleLoadMore = () => { if (nextCursor) void load(true, nextCursor) }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className={className}>
      {/* Section header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-1">
        <span className="text-[10px] font-[800] tracking-[0.14em] uppercase text-[#3a5068]">
          Approval History
          {total > 0 && (
            <span className="ml-1.5 font-normal normal-case tracking-normal">({total})</span>
          )}
        </span>
        <button
          onClick={handleRefresh}
          className="text-[#3a5068] hover:text-[#8fa3bd] transition-colors"
          aria-label="Refresh history"
          title="Refresh"
        >
          <RefreshCw size={12} />
        </button>
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div className="px-4 py-3 flex flex-col gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-3 animate-pulse">
              <div className="w-2.5 h-2.5 rounded-full bg-[rgba(148,163,184,0.12)] mt-1 flex-shrink-0" />
              <div className="flex-1 flex flex-col gap-1.5">
                <div className="h-3 w-3/4 bg-[rgba(148,163,184,0.08)] rounded" />
                <div className="h-2.5 w-1/2 bg-[rgba(148,163,184,0.06)] rounded" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error state */}
      {!loading && error && (
        <div className="mx-4 my-2 px-3 py-2.5 bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)] rounded-[10px] text-[#f87171] text-[11.5px] flex items-center gap-2 flex-wrap">
          <AlertTriangle size={13} aria-hidden="true" />
          <span className="flex-1">{error}</span>
          <button
            onClick={handleRefresh}
            className="underline text-[11.5px] text-[#f87171] bg-none border-none cursor-pointer p-0 font-[inherit]"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && entries.length === 0 && (
        <p className="px-4 py-4 text-[12px] text-[#3a5068] italic text-center">
          No approval history for this run yet.
        </p>
      )}

      {/* Timeline */}
      {!loading && !error && entries.length > 0 && (
        <motion.ol
          variants={listVariants}
          initial="hidden"
          animate="show"
          aria-label="Approval history timeline"
        >
          {entries.map((entry, idx) => {
            const isLast     = idx === entries.length - 1
            const dotColor   = DOT_COLORS[entry.kind]
            const labelColor = LABEL_COLORS[entry.kind]

            return (
              <motion.li
                key={entry.id}
                variants={itemVariants}
                transition={{ duration: 0.18 }}
                className="flex gap-0 px-4 pb-0"
              >
                {/* Rail */}
                <div className="flex flex-col items-center flex-shrink-0 w-5 pt-[2px]">
                  <span
                    className="block w-[9px] h-[9px] rounded-full flex-shrink-0 relative z-[1]"
                    style={{ background: dotColor }}
                  />
                  {!isLast && (
                    <span className="block w-px flex-1 min-h-[14px] bg-[rgba(148,163,184,0.10)] mt-1" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pb-[18px] pl-[10px]">
                  {/* Top row */}
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <span className={`text-[12.5px] font-bold tracking-[0.01em] ${labelColor}`}>
                      {entry.label}
                    </span>
                    <span
                      className="text-[10px] text-[#3a5068] whitespace-nowrap tabular-nums"
                      title={new Date(entry.timestamp).toLocaleString()}
                    >
                      {relTime(entry.timestamp)}
                    </span>
                  </div>

                  {/* Actor chip */}
                  <span className="inline-flex items-center gap-[5px] mt-[3px] bg-[rgba(148,163,184,0.07)] border border-[rgba(148,163,184,0.12)] rounded-[5px] px-[7px] py-[1px]">
                    <span className="text-[9px] font-[800] tracking-[0.1em] uppercase text-[#4e6480]">
                      {entry.actorType}
                    </span>
                    <code className="font-mono text-[10px] text-[#8fa3bd]">{entry.actor}</code>
                  </span>

                  {/* Notes */}
                  {entry.notes && (
                    <p className="mt-1.5 text-[11.5px] text-[#8fa3bd] leading-[1.55] italic">
                      "{entry.notes}"
                    </p>
                  )}
                </div>
              </motion.li>
            )
          })}
        </motion.ol>
      )}

      {/* Load more */}
      {nextCursor && (
        <div className="px-4 pb-3 pt-1 flex justify-center">
          <button
            disabled={loadingMore}
            onClick={handleLoadMore}
            className="inline-flex items-center gap-1.5 bg-[rgba(148,163,184,0.06)] border border-[rgba(148,163,184,0.12)] rounded-lg text-[#8fa3bd] font-[inherit] text-[11.5px] font-bold px-3.5 py-1.5 cursor-pointer transition-colors hover:bg-[rgba(148,163,184,0.11)] hover:text-[#c8d9ee] disabled:opacity-60 disabled:cursor-not-allowed w-full justify-center"
          >
            {loadingMore
              ? <><Loader2 size={12} className="animate-spin" /> Loading…</>
              : 'Load more'
            }
          </button>
        </div>
      )}
    </div>
  )
}
